import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { api } from '@/lib/api'
import { Card, CardContent } from '@/components/ui/card'
import type { Tenant } from '@/pages/Tenants'

export default function Dashboard() {
  const { user, token } = useAuth()
  const isSuper = user?.role === 'SUPER_ADMIN'
  const [debt, setDebt] = useState<{ yellow: number; red: number; restricted: number; suspend: number; amount: number } | null>(null)

  // Semáforo de deuda: cuántos clubes en amarillo / rojo (mismo list.php que Clubes).
  useEffect(() => {
    api<{ ok: boolean; tenants: Tenant[] }>('/tenants/list.php', { method: 'GET', token: token || undefined })
      .then(res => {
        const withDebt = res.tenants.filter(t => t.debt?.count > 0)
        setDebt({
          yellow:  withDebt.filter(t => t.debt.level === 1).length,
          red:     withDebt.filter(t => t.debt.level >= 2).length,
          restricted: withDebt.filter(t => t.debt.level === 4).length,
          suspend: withDebt.filter(t => t.debt.level >= 5).length,
          amount:  withDebt.reduce((s, t) => s + t.debt.amount, 0),
        })
      })
      .catch(() => setDebt(null))
  }, [token])

  return (
    <div className="px-4 sm:px-8 py-8 max-w-6xl">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900">Bienvenido, {user?.name?.split(' ')[0]}</h1>
        <p className="text-sm text-slate-500 mt-1">Panel de administración de LobbyPlay</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Link to="/tenants" className="group">
          <Card className="hover:border-brand transition-colors h-full">
            <CardContent className="p-6">
              <div className="w-10 h-10 rounded-lg bg-red-50 text-red-600 flex items-center justify-center mb-3">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 14h6m-6-4h6M7 3h10a2 2 0 012 2v16l-3-2-2 2-2-2-2 2-2-2-3 2V5a2 2 0 012-2z" />
                </svg>
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">Clubes con deuda</h3>
              {debt === null ? (
                <p className="text-sm text-slate-500">Facturas pendientes por club</p>
              ) : debt.yellow + debt.red === 0 ? (
                <p className="text-sm text-emerald-700">Todos los clubes al día</p>
              ) : (
                <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
                  <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-red-500" />{debt.red} en rojo</span>
                  <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-400" />{debt.yellow} en amarillo</span>
                  <span className="text-slate-400">· ${debt.amount.toFixed(2)} pendiente</span>
                  {debt.restricted + debt.suspend > 0 && (
                    <span className="w-full text-xs font-medium text-red-700">
                      {debt.restricted > 0 && `${debt.restricted} restringido${debt.restricted > 1 ? 's' : ''}`}
                      {debt.restricted > 0 && debt.suspend > 0 && ' · '}
                      {debt.suspend > 0 && `${debt.suspend} para suspender`}
                    </span>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </Link>

        <Link to="/analytics" className="group">
          <Card className="hover:border-brand transition-colors h-full">
            <CardContent className="p-6">
              <div className="w-10 h-10 rounded-lg bg-brand/10 text-brand-dark flex items-center justify-center mb-3">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v18h18M7 14l3-3 4 4 6-7" />
                </svg>
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">Analytics</h3>
              <p className="text-sm text-slate-500">Embudos, KPIs y eventos en tiempo real</p>
            </CardContent>
          </Card>
        </Link>

        {isSuper && (
          <Link to="/admins" className="group">
            <Card className="hover:border-brand transition-colors h-full">
              <CardContent className="p-6">
                <div className="w-10 h-10 rounded-lg bg-brand/10 text-brand-dark flex items-center justify-center mb-3">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-3.13a4 4 0 100-8 4 4 0 000 8zm6 0a3 3 0 100-6 3 3 0 000 6z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">Administradores</h3>
                <p className="text-sm text-slate-500">Invitar, revocar y gestionar roles</p>
              </CardContent>
            </Card>
          </Link>
        )}
      </div>
    </div>
  )
}
